import { Icon, type IconName } from '@/components/Icon';
import { formatNumber } from '@/lib/dashboardFormat';
import type { DashboardKpis } from '@/api/dashboard';

type Trend = 'up' | 'down' | 'flat';

interface Tile {
  label: string;
  value: string;
  icon: IconName;
  note?: string;
  trend?: Trend;
  /** Тон плитки — только там, где число само по себе означает проблему. */
  tone?: 'warn' | 'crit';
}

/**
 * Шесть показателей портфеля. Подписи — про кабинеты и у.е., а не про
 * «лицензии»: лицензии живут на стороне Maintest 5i, админка их не считает.
 */
export function KpiGrid({ kpis }: { kpis: DashboardKpis }) {
  const delta = kpis.newThisMonth - kpis.newPrevMonth;
  const risk = kpis.expired + kpis.expiringSoon;

  const tiles: Tile[] = [
    {
      label: 'Клиентов в портфеле',
      value: formatNumber(kpis.clients),
      icon: 'users',
      note: `${kpis.newThisMonth} новых в этом месяце`,
    },
    {
      label: 'Новых за месяц',
      value: formatNumber(kpis.newThisMonth),
      icon: 'userPlus',
      note: `${delta >= 0 ? '+' : ''}${delta} к прошлому месяцу (${kpis.newPrevMonth})`,
      trend: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
    },
    {
      label: 'Остаток у.е.',
      value: formatNumber(kpis.balanceTotal),
      icon: 'psi',
      note: `Выдано за месяц: ${formatNumber(kpis.ueIssuedThisMonth)}`,
    },
    {
      label: 'Подключено тестов',
      value: formatNumber(kpis.testsConnected),
      icon: 'testChecklist',
      note: 'Разных тестов в портфеле',
    },
    {
      label: 'Сроки кабинетов',
      value: formatNumber(risk),
      icon: 'clock',
      note:
        risk === 0
          ? 'Ничего не истекает в ближайший месяц'
          : `Истекли: ${kpis.expired} · истекают за 30 дней: ${kpis.expiringSoon}`,
      tone: kpis.expired > 0 ? 'crit' : risk > 0 ? 'warn' : undefined,
    },
    {
      label: 'Обращения за месяц',
      value: formatNumber(kpis.openTickets),
      icon: 'support',
      note: 'От пользователей ваших клиентов',
    },
  ];

  return (
    <div className="kpi-grid">
      {tiles.map((tile) => (
        <div className={`kpi-tile${tile.tone ? ` kpi-tile--${tile.tone}` : ''}`} key={tile.label}>
          <div className="kpi-tile__top">
            <span className="kpi-tile__label">{tile.label}</span>
            <Icon name={tile.icon} size={18} className="kpi-tile__icon" />
          </div>
          <div className="kpi-tile__value">
            {tile.value}
            {tile.trend && tile.trend !== 'flat' && (
              <span className={`kpi-tile__trend kpi-tile__trend--${tile.trend}`} aria-hidden="true">
                {tile.trend === 'up' ? '↑' : '↓'}
              </span>
            )}
          </div>
          {tile.note && <div className="kpi-tile__note">{tile.note}</div>}
        </div>
      ))}
    </div>
  );
}
